import { type ReactNode, useEffect, useRef } from "react";

import { AlmaAccountCore } from "@almamedia/alma-account-core";

import { useAuth } from "@/src/auth/hooks/useAuth";
import {
  clearAuthTokens,
  localStorageHasAnyAuthToken,
  localStorageHasPreviousSessionFlag
} from "@/src/auth/utils";

interface AuthInitializationProviderProps {
  children: ReactNode;
}

const AuthInitializationProvider = ({
  children
}: AuthInitializationProviderProps) => {
  const initialLoginAttemptedRef = useRef(false);
  const { attemptLoginOnInitialLoad } = useAuth();

  useEffect(() => {
    const hasPreviousSessionFlag = localStorageHasPreviousSessionFlag();
    const hasAnyAuthToken = localStorageHasAnyAuthToken();
    if (hasAnyAuthToken && !hasPreviousSessionFlag) {
      clearAuthTokens();
    }
    if (initialLoginAttemptedRef.current) return;
    const attemptLogin = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const code = searchParams.get("code");
      const state = searchParams.get("state");

      // Handle SSO callback
      if (code && state) {
        try {
          const core = AlmaAccountCore.getInstance();
          await core.handleAuthentication();

          // Get the stored connection from localStorage
          const storedConnection = localStorage.getItem("AUTH_CONNECTION");
          if (!storedConnection) {
            throw new Error("No connection stored for SSO callback");
          }

          window.location.replace("/dealer/all-active");
          return;
        } catch (error) {
          console.error("Failed to handle SSO callback:", error);
          window.location.replace("/dealer");
        }
      }

      // Normal initialization flow
      if (!initialLoginAttemptedRef.current) {
        initialLoginAttemptedRef.current = true;
        const routeType = globalThis.location.pathname.startsWith("/dealer")
          ? "dealer"
          : globalThis.location.pathname.startsWith("/admin")
            ? "admin"
            : "consumer";
        const connection =
          localStorage.getItem("AUTH_CONNECTION") ||
          searchParams.get("connection");
        await attemptLoginOnInitialLoad(routeType, connection);
      }
    };

    attemptLogin();
  }, [attemptLoginOnInitialLoad]);

  return children;
};

export { AuthInitializationProvider };
